import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formMasterAPI, dashboardAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ChartComponent from '../../components/common/ChartComponent';
import StatCard from '../../components/common/StatCard';

const FormMasterDashboard = () => {
  const { user } = useAuth();
  const [myClasses, setMyClasses] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [currentSession, setCurrentSession] = useState({});
  const [currentTerm, setCurrentTerm] = useState({});

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const statsRes = await dashboardAPI.getFormMasterStats();
      const statsData = statsRes.data;
      
      setStats(statsData);
      setMyClasses(statsData.class_distribution || []);
      setPerformanceData(statsData.performance_data || []);
      setCurrentSession(statsData.current_session || {});
      setCurrentTerm(statsData.current_term || {});
      
      // Format recent activities from API
      if (statsData.recent_activities) {
        const formattedActivities = statsData.recent_activities.map((activity, index) => {
          let action = '';
          let icon = 'fa-chart-line';
          let time = formatTimeAgo(activity.timestamp);
          
          switch(activity.type) {
            case 'grade_update':
              action = `Updated grades for ${activity.student_name} (${activity.class_name})`;
              icon = 'fa-chart-line';
              break;
            case 'comment_added':
              action = `Added comment for ${activity.student_name}`;
              icon = 'fa-comment';
              break;
            case 'report_generated':
              action = `Generated report for ${activity.class_name}`;
              icon = 'fa-file-pdf';
              break;
            default:
              action = `Updated ${activity.class_name}`;
          }
          
          return {
            id: index + 1,
            action,
            time,
            class: activity.class_name || 'N/A',
            icon,
            details: activity
          };
        });
        setRecentActivities(formattedActivities);
      }

    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return past.toLocaleDateString();
  };

  const handleCalculateGrades = (classId) => {
    window.location.href = `/form-master/calculate-grades?class=${classId}`;
  };

  const handleGenerateReport = (classId) => {
    window.location.href = `/form-master/reports?class=${classId}`;
  };

  const getPerformanceChartData = () => {
    if (!performanceData || performanceData.length === 0) {
      return null;
    }
    
    return {
      labels: performanceData.map(item => item.class_name),
      datasets: [
        {
          label: 'Average Score',
          data: performanceData.map(item => item.average_score),
          backgroundColor: '#4f46e5',
          borderColor: '#3730a3',
          borderWidth: 2,
        },
        {
          label: 'Top Score',
          data: performanceData.map(item => item.top_score),
          backgroundColor: '#10b981',
          borderColor: '#047857',
          borderWidth: 2,
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Welcome back, {user?.name}!</h1>
            <p className="opacity-90 mt-1">Form Master Dashboard - Manage your assigned classes</p>
            <div className="flex items-center mt-2 text-sm opacity-90">
              <i className="fas fa-calendar-alt mr-2"></i>
              <span>
                Current Session: {currentSession.name || 'Not set'} • 
                Term: {currentTerm.name || 'Not set'}
              </span>
            </div>
          </div>
          <div className="hidden md:block">
            <i className="fas fa-chalkboard-teacher text-5xl opacity-20"></i>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Assigned Classes"
          value={stats.total_classes || myClasses.length || 0}
          icon="fa-school"
          color="primary"
          change={null}
        />
        <StatCard
          title="Total Students"
          value={stats.total_students || 0}
          icon="fa-users"
          color="green"
          change={null}
        />
        <StatCard
          title="Pending Grading"
          value={stats.classes_pending_grading || 0}
          icon="fa-tasks"
          color="yellow"
          subtitle="classes need grading"
        />
        <StatCard
          title="Class Average"
          value={stats.performance_overview?.overall_average || '0.0'}
          icon="fa-chart-line"
          color="purple"
          suffix="/100"
          subtitle="across all classes"
        />
      </div>

      {/* My Classes */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">My Assigned Classes</h3>
              <p className="text-gray-600 text-sm">Manage grades, comments, and reports for your classes</p>
            </div>
            <div className="text-sm text-gray-500">
              {stats.total_classes || myClasses.length} classes assigned
            </div>
          </div>
        </div>

        <div className="p-6">
          {myClasses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myClasses.map((cls) => (
                <div key={cls.class_id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-xl font-bold text-gray-800">{cls.class_name}</h4>
                        <p className="text-gray-600 text-sm">Class ID: {cls.class_id}</p>
                      </div>
                      <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                        {cls.student_count || 0} students
                      </span>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Assigned Since:</span>
                        <span className="font-medium">
                          {cls.assigned_since ? new Date(cls.assigned_since).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      
                      {/* Show class performance if available */}
                      {performanceData.find(p => p.class_id === cls.class_id) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Avg Score:</span>
                          <span className="font-medium text-green-600">
                            {performanceData.find(p => p.class_id === cls.class_id)?.average_score || 'N/A'}%
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={() => handleCalculateGrades(cls.class_id)}
                        className="w-full btn-primary py-2 flex items-center justify-center"
                      >
                        <i className="fas fa-calculator mr-2"></i>
                        Calculate Grades
                      </button>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <a
                          href={`/form-master/class-students?class=${cls.class_id}`}
                          className="btn-secondary py-2 text-sm flex items-center justify-center"

                             style={{ textDecoration: "none" }}
                        >
                          <i className="fas fa-users mr-2"></i>
                          View Students
                        </a>
                        <button
                          onClick={() => handleGenerateReport(cls.class_id)}
                          className="bg-green-600 text-white py-2 text-sm rounded-lg hover:bg-green-700 flex items-center justify-center"
                        >
                          <i className="fas fa-file-pdf mr-2"></i>
                          Generate Report
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="fas fa-school text-4xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Classes Assigned</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                You haven't been assigned any classes yet. Please contact the administrator to get class assignments.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Performance Overview */}
      {performanceData && performanceData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Class Performance Overview</h3>
          
          {/* Best Performing Class */}
          {stats.performance_overview?.best_performing_class && (
            <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center">
                    <i className="fas fa-trophy text-yellow-500 text-xl mr-3"></i>
                    <div>
                      <h4 className="font-bold text-gray-900">Best Performing Class</h4>
                      <p className="text-sm text-gray-600">
                        {stats.performance_overview.best_performing_class.class_name} • 
                        Avg: {stats.performance_overview.best_performing_class.average_score}%
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-green-600">
                    {stats.performance_overview.best_performing_class.top_score}%
                  </div>
                  <div className="text-sm text-gray-500">Top Score</div>
                </div>
              </div>
            </div>
          )}

          {/* Performance Chart */}
          <div className="h-64">
            <ChartComponent
              type="bar"
              data={getPerformanceChartData()}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: true,
                    text: 'Class Performance Comparison'
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 100,
                    title: {
                      display: true,
                      text: 'Score (%)'
                    }
                  }
                }
              }}
            />
          </div>

          {/* Performance Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {stats.performance_overview?.overall_average || '0.0'}%
              </div>
              <div className="text-sm text-gray-500">Overall Average</div>
            </div>
            <div className="border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats.performance_overview?.total_classes_graded || 0}
              </div>
              <div className="text-sm text-gray-500">Classes Graded</div>
            </div>
            <div className="border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">
                {stats.performance_overview?.average_students_per_class || '0.0'}
              </div>
              <div className="text-sm text-gray-500">Avg Students/Class</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activities */}
      {recentActivities.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Recent Activities</h3>
            <span className="text-sm text-gray-500">
              {stats.recent_grade_updates || 0} updates today
            </span>
          </div>
          <div className="space-y-3">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                    <i className={`fas ${activity.icon}`}></i>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{activity.action}</div>
                    <div className="text-sm text-gray-500">
                      Class: {activity.class} • {activity.time}
                    </div>
                    {activity.details?.average && (
                      <div className="text-xs text-gray-400 mt-1">
                        Score: {activity.details.average}% • Position: {activity.details.position}
                      </div>
                    )}
                  </div>
                </div>
                <a
                  href={`/form-master/class-students?class=${activity.details?.class_name || ''}`}
                  className="text-primary-600 hover:text-primary-800"
                >
                  <i className="fas fa-arrow-right"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <a
            href="/form-master/calculate-grades"
            className="bg-primary-50 border border-primary-200 rounded-lg p-4 text-center hover:bg-primary-100 transition-colors"

               style={{ textDecoration: "none" }}
          >
            <div className="p-3 bg-primary-100 rounded-lg inline-flex mb-3">
              <i className="fas fa-calculator text-primary-600 text-xl"></i>
            </div>
            <h4 className="font-medium text-gray-900">Calculate Grades</h4>
            <p className="text-sm text-gray-500 mt-1">Compute term results</p>
            <div className="text-xs text-primary-600 mt-2">
              {stats.classes_pending_grading || 0} pending
            </div>
          </a>

          <a
            href="/form-master/class-students"
            className="bg-green-50 border border-green-200 rounded-lg p-4 text-center hover:bg-green-100 transition-colors"

               style={{ textDecoration: "none" }}
          >
            <div className="p-3 bg-green-100 rounded-lg inline-flex mb-3">
              <i className="fas fa-users text-green-600 text-xl"></i>
            </div>
            <h4 className="font-medium text-gray-900">View Students</h4>
            <p className="text-sm text-gray-500 mt-1">Manage class roster</p>
            <div className="text-xs text-green-600 mt-2">
              {stats.total_students || 0} total students
            </div>
          </a>

          {/* <a
            href="/form-master/reports"
            className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center hover:bg-yellow-100 transition-colors"
          >
            <div className="p-3 bg-yellow-100 rounded-lg inline-flex mb-3">
              <i className="fas fa-chart-bar text-yellow-600 text-xl"></i>
            </div>
            <h4 className="font-medium text-gray-900">Generate Reports</h4>
            <p className="text-sm text-gray-500 mt-1">Create class reports</p>
            <div className="text-xs text-yellow-600 mt-2">
              {stats.performance_overview?.total_classes_graded || 0} classes graded
            </div>
          </a> */}

          <a
            href="/form-master/class-students"
            className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center hover:bg-purple-100 transition-colors"
             style={{ textDecoration: "none" }}
          >
            <div className="p-3 bg-purple-100 rounded-lg inline-flex mb-3">
              <i className="fas fa-comment-alt text-purple-600 text-xl"></i>
            </div>
            <h4 className="font-medium text-gray-900">Add Comments</h4>
            <p className="text-sm text-gray-500 mt-1">Student feedback</p>
            <div className="text-xs text-purple-600 mt-2">
              {stats.total_pending_comments || 0} pending
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default FormMasterDashboard;